import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RegistryDealSetup, RegistrySetupOpportunity } from '../../api/registryDealTypes';
import { errorMessage } from '../../api/registryApi';
import { SETUP_SECTIONS } from '../../domain/setup/sections';
import { compactMoney, date } from '../../lib/format';
import { hasRegistryPermission, useRegistrySession } from '../../session/RegistrySession';
import { EmptyState, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';

export function stepsDone(setup: Pick<RegistryDealSetup, 'status' | 'completed'>): number {
  return SETUP_SECTIONS.filter(
    (section) =>
      (section.group !== 'OFFER' && setup.status !== 'DRAFT') ||
      setup.completed.includes(section.id),
  ).length;
}

export function opportunityTerms(opportunity: RegistrySetupOpportunity): string {
  const size = Number(opportunity.fundSize);
  const roi = Number(opportunity.returnOnInvestment);
  return [
    opportunity.fundSize && Number.isFinite(size) ? `Target ${compactMoney(size)}` : '',
    opportunity.returnOnInvestment && Number.isFinite(roi) ? `${roi}% p.a.` : '',
    opportunity.closeDate ? `closes ${date(opportunity.closeDate)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

const CARD_GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
};

function InProgress({ setups, open }: { setups: RegistryDealSetup[]; open: (id: string) => void }) {
  if (!setups.length) return null;
  return (
    <SectionCard title="In progress" sx={{ mb: 3 }}>
      <Box sx={CARD_GRID}>
        {setups.map((setup) => {
          const done = stepsDone(setup);
          return (
            <Paper
              key={setup.externalId}
              variant="outlined"
              sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 1, flex: 1 }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {String(setup.values?.['fund.name'] || setup.fundOpportunity.name)}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <StatusChip status={setup.status} />
                </Box>
              </Stack>
              <Typography variant="caption" component="div">
                {done} of {SETUP_SECTIONS.length} steps · updated {date(setup.updatedAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Started by {setup.createdBy.name ?? 'Unknown'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(done / SETUP_SECTIONS.length) * 100}
                aria-label={`${done} of ${SETUP_SECTIONS.length} steps done`}
                sx={{
                  my: 1.5,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              />
              <Button variant="outlined" onClick={() => open(setup.externalId)}>
                {setup.status === 'PUBLISHED' ? 'View' : 'Continue'}
              </Button>
            </Paper>
          );
        })}
      </Box>
    </SectionCard>
  );
}

function OpportunityCards({
  opportunities,
  busy,
  canStart,
  start,
}: {
  opportunities: RegistrySetupOpportunity[];
  busy: string;
  canStart: boolean;
  start: (id: string) => void;
}) {
  return (
    <SectionCard title="Approved opportunities">
      {opportunities.length === 0 ? (
        <EmptyState title="No opportunities waiting for setup" />
      ) : (
        <Box sx={CARD_GRID}>
          {opportunities.map((opportunity) => (
            <Paper
              key={opportunity.externalId}
              variant="outlined"
              sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
            >
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{opportunity.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
                {opportunity.summaryTitle}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 1.5 }}>
                {opportunityTerms(opportunity)}
              </Typography>
              <Button
                variant="contained"
                disabled={!canStart || busy === opportunity.externalId}
                onClick={() => start(opportunity.externalId)}
              >
                Start setup
              </Button>
            </Paper>
          ))}
        </Box>
      )}
    </SectionCard>
  );
}

export default function LiveSetupHomePage() {
  const session = useRegistrySession()!;
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<RegistrySetupOpportunity[]>([]);
  const [setups, setSetups] = useState<RegistryDealSetup[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    Promise.all([session.api.listSetupOpportunities(), session.api.listSetups()])
      .then(([available, existing]) => {
        setOpportunities(available);
        setSetups(existing);
      })
      .catch((failure) => setError(errorMessage(failure)));
  }, [session]);

  const start = async (id: string) => {
    setBusy(id);
    setError('');
    try {
      const setup = await session.api.startSetup(id);
      navigate(`/manager/setup/${setup.externalId}`);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Live from Fides"
        title="Establish fund"
        subtitle="Review fund in progress or create a new fund from an approved opportunity"
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <InProgress setups={setups} open={(id) => navigate(`/manager/setup/${id}`)} />
      <OpportunityCards
        opportunities={opportunities.filter((opportunity) => !opportunity.setupExternalId)}
        busy={busy}
        canStart={hasRegistryPermission(session, 'RegistrySetup')}
        start={start}
      />
    </>
  );
}
